import React, { useState, useEffect } from "react";
import { doc, updateDoc, getFirestore, getDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import Loading from "./Loading";
import GuardMulti from "./GuardMulti";
import BarSelect from "./BarSelect";
import { Sheet, Chip } from "./DispatchModal";
import { fmtHM, fmtDate, fmtDur, getMillis } from "./helpers";
import { schema } from "../schema";
import { resolveName, nameKey } from "../utils/names";

function Sec({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: "block", fontSize: 10, fontWeight: 700,
        color: "var(--subtle)", marginBottom: 6,
        textTransform: "uppercase", letterSpacing: ".04em",
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * CloseAlarmModal — Step 2: close an alarm.
 * Adaptive form; writes new fields additively while keeping legacy fields unchanged.
 *
 * Props:
 *   alarmObj: Firestore alarm doc
 *   facilityId: string
 *   operators: string[]
 *   knownGuards: string[]
 *   nameAliases: object  (facility.nameAliases — variantes → canónico)
 *   showSiren: bool  (from facility config)
 *   isReopen: bool   (true if reopening a closed event)
 *   reviewer: { canReview, name, uid } — si quien edita es revisor (Fase 2),
 *     la edición marca el evento como "corregido"; si edita un operador,
 *     se limpia la revisión/validación previa (debe re-revisarse).
 *   onClose: () => void
 */
export default function CloseAlarmModal({
  alarmObj,
  facilityId,
  operators = [],
  knownGuards = [],
  nameAliases,
  showSiren = false,
  isReopen = false,
  reviewer,
  onClose,
}) {
  const db = getFirestore();
  const [loading, setLoading] = useState(true);

  // ── Form state ──
  const [op, setOp]             = useState("");
  const [situation, setSituation] = useState("");
  const [guards, setGuards]     = useState([]);
  const [bars, setBars]         = useState(null);
  const [heardAlarm, setHeardAlarm] = useState(null);
  const [hasVeh, setHasVeh]     = useState(null);
  const [plate, setPlate]       = useState("");
  const [vehDesc, setVehDesc]   = useState("");
  const [hasSusp, setHasSusp]   = useState(null);
  const [suspDesc, setSuspDesc] = useState("");
  const [comments, setComments] = useState("");
  const [lateR, setLateR]       = useState("");
  const [quickR, setQuickR]     = useState("");
  const [showMoreOps, setShowMoreOps] = useState(false);
  const ops = operators || [];

  // ── Timestamps from alarm ──
  const createdMs     = alarmObj.createdAt?.seconds ? alarmObj.createdAt.seconds * 1000 : null;
  const dispatchMs    = alarmObj.dispatchTime ? getMillis(alarmObj.dispatchTime) : null;
  const arrivedMs     = alarmObj.arrivedTime  ? getMillis(alarmObj.arrivedTime)  : null;

  const isLate     = !isReopen && createdMs && (Date.now() - createdMs) > 7200000;
  const isVeryLate = !isReopen && createdMs && (Date.now() - createdMs) > 86400000; // > 24 hours
  const isQuick    = !isReopen && createdMs && (Date.now() - createdMs) < 300000;

  const dispatchDelta = dispatchMs && createdMs ? Math.max(0, Math.floor((dispatchMs - createdMs) / 1000)) : null;
  const arrivalDelta  = arrivedMs && dispatchMs  ? Math.max(0, Math.floor((arrivedMs  - dispatchMs)  / 1000)) : null;

  // RT State Logic
  const updatedMs = alarmObj.updatedAt?.seconds ? alarmObj.updatedAt.seconds * 1000 : null;
  const isAttackActive = updatedMs && (Date.now() - updatedMs) < 30000;
  let rtState = "detenido";
  let rtColor = "#6b7280"; // Gray
  let rtLabel = "Ataque detenido";
  let rtPulse = false;

  if (isAttackActive) {
    const elapsed = createdMs ? (updatedMs - createdMs) : 0;
    const durMs = (alarmObj.duration || 0) * 1000;
    if (elapsed > durMs + 30000) {
      rtState = "intermitente";
      rtColor = "#f97316"; // Orange
      rtLabel = "Ataque intermitente";
      rtPulse = true;
    } else {
      rtState = "activo";
      rtColor = "#dc2626"; // Red
      rtLabel = "Ataque activo";
      rtPulse = true;
    }
  }

  // Calculation of result based on situation
  let computedResult = "";
  if (situation === "1") computedResult = "drill";
  else if (situation === "2") computedResult = "unmanaged";
  else if (situation === "3") computedResult = "without_findings";
  else if (["4","5","6","7"].includes(situation)) computedResult = "with_findings";

  const result = computedResult;
  const isDrill = result === "drill";
  const showSirenField = showSiren && result && !isDrill;

  // Plate Validation
  const isValidPlate = (!hasVeh || plate === "" || /^[A-Z0-9]{4}[0-9]{2}$/.test(plate));

  const ok = Boolean(
    op && result && isValidPlate &&
    (isLate ? lateR : true) &&
    (isQuick && !isDrill ? quickR : true) &&
    (result === "with_findings" || result === "resolved" ? (comments || "").trim() : true)
  );

  // Load existing data (for reopen)
  useEffect(() => {
    async function load() {
      try {
        const ref  = doc(db, schema.groupedAlarms, alarmObj.docID);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const d = snap.data();
          setOp(d.closedBy || d.guardName || "");
          // Derive old situation from result (hacky backward compatibility for edits)
          const legacyMap = { exercise: "drill", resolved: "with_findings", false_alarm: "without_findings" };
          const loadedRes = d.closeResult || legacyMap[d.reasonForResolution] || "";
          
          if (d.situation) setSituation(d.situation);
          else if (loadedRes === "drill") setSituation("1");
          else if (loadedRes === "unmanaged") setSituation("2");
          else if (loadedRes === "without_findings" || loadedRes === "not_found" || loadedRes === "brief_unverifiable" || loadedRes === "signal_not_found") setSituation("3");
          else if (loadedRes === "with_findings" || loadedRes === "successful" || loadedRes === "resolved" || loadedRes === "deterrence" || loadedRes === "verified_unlocated" || loadedRes === "out_of_norm") setSituation("4");

          setGuards(Array.isArray(d.guards) ? d.guards : d.guardName ? [d.guardName] : []);
          setBars(typeof d.signalBars === "number" ? d.signalBars : null);
          setHeardAlarm(d.heardAlarm ?? null);
          setComments(d.notes || "");
          setPlate(d.plate || d.vehicle?.plate || "");
          setVehDesc(d.vehicle?.desc || "");
          setHasVeh(d.vehicle ? true : null);
          setHasSusp(d.suspect ?? null);
          setSuspDesc(d.suspectDesc || "");
          setLateR(d.lateReason || "");
          setQuickR(d.quickCloseReason || "");

          // If it's a new closure of a very old event, auto-declare as "Guardia no asistió"
          if (!isReopen && isVeryLate && !d.situation) {
            setSituation("2");
          }
        }
      } catch (e) {
        console.error("CloseAlarmModal load error:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmObj.docID]);

  async function handleSubmit() {
    if (!ok) return;
    const ref = doc(db, schema.groupedAlarms, alarmObj.docID);
    const now = serverTimestamp();

    // Normaliza + aplica aliases del recinto + reutiliza variantes existentes
    // (match sin tildes/mayúsculas) para no crear nombres duplicados.
    const cleanOp = resolveName(op, nameAliases?.operators, operators);

    const opsArr = Array.isArray(operators) ? operators : [];
    if (cleanOp && !opsArr.some(o => nameKey(o) === nameKey(cleanOp))) {
      try {
        await updateDoc(doc(db, `Facilities/${facilityId}`), { operators: arrayUnion(cleanOp) });
      } catch (e) {
        console.error("Failed to add new operator to facility", e);
      }
    }

    // Guardias: resolver cada uno y deduplicar por clave de matching
    const seenKeys = new Set();
    const cleanGuards = [];
    for (const g of guards) {
      const resolved = resolveName(g, nameAliases?.guards, knownGuards);
      const k = nameKey(resolved);
      if (k && !seenKeys.has(k)) {
        seenKeys.add(k);
        cleanGuards.push(resolved);
      }
    }

    // Persist new guards to facility document (same pattern as operators above)
    for (const g of cleanGuards) {
      if (g && !(knownGuards || []).some(kg => nameKey(kg) === nameKey(g))) {
        try {
          await updateDoc(doc(db, `Facilities/${facilityId}`), { guards: arrayUnion(g) });
        } catch (e) {
          console.error("Failed to add new guard to facility", e);
        }
      }
    }

    // Map new result → legacy reasonForResolution (backward compat)
    const legacyReason = { drill: "exercise", with_findings: "resolved", without_findings: "false_alarm", unmanaged: "resolved_automatically" }[result] || result;

    const update = {
      // ── Legacy fields (unchanged keys) ──
      reasonForResolution: legacyReason,
      notes: comments || "",
      plate: hasVeh ? plate : "",
      guardName: cleanGuards[0] || cleanOp || "",
      signalBars: bars != null ? String(bars) : "not_specified",
      heardAlarm: showSirenField ? heardAlarm : null,
      resolvedAt: alarmObj.resolvedAt || now,
      resolvedUpdatedAt: now,

      // ── New fields (additive) ──
      situation: situation, // 1-7 code
      closeResult: result, // new enum computed
      closedBy: cleanOp,
      guards: cleanGuards,
      signalBarsNum: bars,
      vehicle: hasVeh ? { plate, desc: vehDesc } : null,
      suspect: hasSusp,
      suspectDesc: hasSusp ? suspDesc : null,
      lateReason: isLate ? lateR : null,
      quickCloseReason: isQuick ? quickR : null,
      closedAt: now,
    };

    // ── Estado de revisión (Fase 2) — solo al editar un evento ya cerrado ──
    if (isReopen) {
      if (reviewer?.canReview) {
        // Corrección hecha por un revisor: queda revisado como "corregido".
        // Cualquier validación previa se invalida (la evidencia debe rehacerse).
        update.reviewStatus = "edited";
        update.reviewedBy = reviewer.name || null;
        update.reviewedByUID = reviewer.uid || null;
        update.reviewedAt = now;
        update.validatedBy = null;
        update.validatedByUID = null;
        update.validatedAt = null;
      } else {
        // Edición de un operador: el evento vuelve a "sin revisar".
        update.reviewStatus = null;
        update.reviewedBy = null;
        update.reviewedByUID = null;
        update.reviewedAt = null;
        update.validatedBy = null;
        update.validatedByUID = null;
        update.validatedAt = null;
      }
    }

    await updateDoc(ref, update);
    onClose();
  }

  return (
    <Sheet
      onClose={onClose}
      header={
        <div style={{ position: "relative" }}>
          {/* Embedded status & duration badge in the top-right corner of the sheet */}
          <div style={{
            position: "absolute",
            top: -24,
            right: -16,
            padding: "4px 10px",
            background: rtColor,
            color: rtState === "intermitente" ? "#000" : "#fff",
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: "0.04em",
            fontFamily: "var(--mono)",
            borderRadius: "0 14px 0 8px", // top-right matches sheet's border radius!
            display: "flex",
            alignItems: "center",
            gap: 6,
            zIndex: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              {rtPulse && <span style={{ width: 4, height: 4, borderRadius: "50%", background: rtState === "intermitente" ? "#000" : "#fff", animation: "pulse 1.5s infinite" }} />}
              <span>{rtLabel.toUpperCase()}</span>
            </div>
            {alarmObj.duration != null && (
              <>
                <span style={{ opacity: 0.5 }}>|</span>
                <span>⏱ {fmtDur(alarmObj.duration)}{rtState === "activo" && " ↑"}</span>
              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
            {/* Left Side: Title & Event details */}
            <div style={{ minWidth: 0, flex: 1, paddingRight: 180 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "var(--bright)", marginBottom: 4 }}>
                {isReopen ? "Editar evento" : "Cerrar evento"}
              </div>
              <div style={{
                display: "flex", gap: "4px 8px", flexWrap: "wrap",
                fontSize: 10, color: "var(--muted)",
              }}>
                <span style={{ fontWeight: 700, color: "var(--subtle)" }}>{alarmObj.zoneName}</span>
                {createdMs && <span>🕐 {fmtDate(createdMs)} {fmtHM(createdMs)}</span>}
                {alarmObj.docID && <span style={{ fontFamily: "var(--mono)", fontSize: 9 }}>ID: {alarmObj.docID}</span>}
              </div>
            </div>
          </div>
        </div>
      }
    >

      {isLate && (
        <div style={{
          padding: "8px 10px", background: "#f59e0b10", border: "1px solid #f59e0b30",
          borderRadius: 7, marginBottom: 12, fontSize: 10, color: "#fbbf24",
        }}>
          <b>Evento antiguo</b> — Indicar motivo de gestión tardía.
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "30px 0" }}>
          <div style={{ width: 32 }}><Loading size="8" /></div>
        </div>
      ) : (
        <>
          {/* Operator */}
          <Sec label="¿Quién gestiona? *">
            {ops.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                {ops.slice(0, 4).map(o => <Chip key={o} sel={op === o} onClick={() => setOp(o)} color="#3b82f6">{o}</Chip>)}
                {ops.length > 4 && (
                  !showMoreOps ? (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setShowMoreOps(true); }}
                      style={{
                        padding: "6px 12px", fontSize: 10, fontWeight: 700,
                        background: "transparent", color: "var(--bright)",
                        border: "1px dashed #3b82f640", borderRadius: 6,
                        cursor: "pointer", transition: "all 0.2s"
                      }}
                    >
                      + Más...
                    </button>
                  ) : (
                    ops.slice(4).map(o => (
                      <Chip key={o} sel={op === o} onClick={() => setOp(o)} color="#3b82f6">{o}</Chip>
                    ))
                  )
                )}
              </div>
            )}
            <input className="v3-input" placeholder="Nombre del operador..." value={op} onChange={e => setOp(e.target.value)} />
          </Sec>

          {/* Result / Situation */}
          <Sec label="Situación encontrada *">
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Chip sel={situation === "1"} onClick={() => setSituation("1")} color="#a855f7">Simulacro / Prueba de sistema</Chip>
              <Chip sel={situation === "2"} onClick={() => setSituation("2")} color="#ef4444">Guardia no asistió</Chip>
              <Chip sel={situation === "3"} onClick={() => setSituation("3")} color="#64748b">Asistió, señal ya había cesado</Chip>
              <Chip sel={situation === "4"} onClick={() => setSituation("4")} color="#22c55e">Señal se perdió al acercarse</Chip>
              <Chip sel={situation === "5"} onClick={() => setSituation("5")} color="#22c55e">Señal persistente, sin localizar</Chip>
              <Chip sel={situation === "6"} onClick={() => setSituation("6")} color="#22c55e">Sospechoso identificado</Chip>
              <Chip sel={situation === "7"} onClick={() => setSituation("7")} color="#22c55e">Fuente conocida / ambiental</Chip>
            </div>
            {result && (
              <div style={{ marginTop: 8, padding: "8px 10px", background: "var(--bg)", borderRadius: 6, fontSize: 11, border: "1px solid var(--border)" }}>
                <span style={{ color: "var(--muted)", fontWeight: 700, marginRight: 6 }}>Resultado de cierre:</span>
                <span style={{ color: "var(--bright)", fontWeight: 800 }}>
                  {
                    {
                      unmanaged: "Sin gestión", without_findings: "Gestión sin hallazgo", 
                      with_findings: "Gestión con hallazgo", drill: "Simulacro"
                    }[result] || result
                  }
                </span>
              </div>
            )}
          </Sec>

          {/* Quick close reason */}
          {isQuick && !isDrill && (
            <Sec label="Cierre rápido — Motivo *">
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {[
                  "Confirmé por cámara que no hay amenaza",
                  "Guardia confirmó zona despejada",
                  "Señal cesó inmediatamente",
                ].map(r => <Chip key={r} sel={quickR === r} onClick={() => setQuickR(r)} color="#f97316">{r}</Chip>)}
              </div>
            </Sec>
          )}

          {/* Late reason */}
          {isLate && (
            <Sec label="¿Por qué no se gestionó a tiempo? *">
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {["No vi la alerta", "No había personal", "Cambio de turno", "Problemas técnicos", "Otro"].map(r => (
                  <Chip key={r} sel={lateR === r} onClick={() => setLateR(r)} color="#f59e0b">{r}</Chip>
                ))}
              </div>
            </Sec>
          )}

          {/* Fields hidden for Simulacro and No-Response cases */}
          {!["drill", "unmanaged"].includes(result) && result && (
            <>
              <Sec label="¿Qué guardia(s) respondieron?">
                <GuardMulti guards={guards} onChange={setGuards} known={knownGuards} facilityId={facilityId} aliases={nameAliases?.guards} />
              </Sec>

              {showSirenField && (
                <Sec label="¿Escuchó la sirena?">
                  <div style={{ display: "flex", gap: 6 }}>
                    <Chip sel={heardAlarm === true}  onClick={() => setHeardAlarm(true)}  color="#22c55e">Sí</Chip>
                    <Chip sel={heardAlarm === false} onClick={() => setHeardAlarm(false)} color="#ef4444">No</Chip>
                  </div>
                </Sec>
              )}

              {/* Signal bars — hidden for old events */}
              {!isLate && (
                <Sec label="Barras de señal del detector">
                  <BarSelect value={bars} onChange={setBars} />
                </Sec>
              )}

              <Sec label="¿Se identificó vehículo?">
                <div style={{ display: "flex", gap: 6, marginBottom: hasVeh ? 8 : 0 }}>
                  <Chip sel={hasVeh === true}  onClick={() => setHasVeh(true)}  color="#3b82f6">Sí</Chip>
                  <Chip sel={hasVeh === false} onClick={() => setHasVeh(false)} color="var(--muted)">No</Chip>
                </div>
                {hasVeh && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div>
                      <input 
                        className="v3-input" 
                        value={plate} 
                        onChange={e => setPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} 
                        placeholder="Patente (ej: AABB11)" 
                        style={{ borderColor: plate && !isValidPlate ? "#ef4444" : undefined }}
                        maxLength={6}
                      />
                      {plate && !isValidPlate && <div style={{ fontSize: 9, color: "#ef4444", marginTop: 4, fontWeight: 600 }}>Deben ser 4 letras/números y terminar en 2 números.</div>}
                    </div>
                    <input className="v3-input" value={vehDesc} onChange={e => setVehDesc(e.target.value)} placeholder="Color, marca, modelo" />
                  </div>
                )}
              </Sec>

              <Sec label="¿Persona sospechosa?">
                <div style={{ display: "flex", gap: 6, marginBottom: hasSusp ? 8 : 0 }}>
                  <Chip sel={hasSusp === true}  onClick={() => setHasSusp(true)}  color="#3b82f6">Sí</Chip>
                  <Chip sel={hasSusp === false} onClick={() => setHasSusp(false)} color="var(--muted)">No</Chip>
                </div>
                {hasSusp && (
                  <textarea
                    className="v3-input"
                    value={suspDesc}
                    onChange={e => setSuspDesc(e.target.value)}
                    placeholder="Género, edad, vestimenta, dirección..."
                    rows={2}
                    style={{ resize: "vertical" }}
                  />
                )}
              </Sec>
            </>
          )}

          {/* Comments — mandatory for "Exitoso" */}
          <Sec label={`Comentarios${result === "resolved" ? " *" : ""}`}>
            <textarea
              className="v3-input"
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder={result === "with_findings" || result === "resolved"
                ? "Resumen de hallazgos, acciones, contacto con autoridades... *"
                : "Información adicional (opcional)"}
              rows={3}
              style={{ resize: "vertical" }}
            />
          </Sec>

          {/* Timing info */}
          {(dispatchDelta != null || arrivalDelta != null) && (
            <div style={{
              padding: "6px 8px", background: "var(--bg)", borderRadius: 7,
              marginBottom: 14, fontSize: 10, display: "flex", gap: 14,
              color: "var(--muted)", flexWrap: "wrap",
            }}>
              {dispatchDelta != null && (
                <span>CCTV despacho: <b style={{ color: "var(--subtle)", fontFamily: "var(--mono)" }}>{fmtDur(dispatchDelta)}</b></span>
              )}
              {arrivalDelta != null && (
                <span>Guardia llegada: <b style={{ color: "#3b82f6", fontFamily: "var(--mono)" }}>{fmtDur(arrivalDelta)}</b></span>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!ok}
            style={{
              width: "100%", padding: 13, border: "none", borderRadius: 9,
              background: ok ? "#22c55e" : "var(--border)",
              color: ok ? "#fff" : "var(--muted)",
              fontSize: 13, fontWeight: 700,
              cursor: ok ? "pointer" : "not-allowed",
            }}
          >
            {isReopen ? "Guardar cambios" : "Guardar y cerrar"}
          </button>
        </>
      )}
    </Sheet>
  );
}
